import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  api, ApiError,
  type NotificationTemplate, type NotificationsView, type OutboxRow,
} from '../../api';

const RECIPIENTS: Record<string, string> = {
  related_user: 'the person it happened to',
  course_instructors: "the course's instructors",
  admins: 'administrators',
  specific: 'named people',
};

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

export function Notifications() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'templates';
  const editing = params.get('edit');

  const [view, setView] = useState<NotificationsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setView(await api.get<NotificationsView>('/admin/notifications')); }
    catch (e) { setError((e as ApiError).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  if (error && !view) return <p className="auth__error">{error}</p>;
  if (!view) return <p className="muted">Loading…</p>;

  const toggle = async (id: number, active: boolean) => {
    setBusy(true); setError(null); setNote(null);
    try {
      await api.patch(`/admin/notifications/${id}`, { active });
      setNote(active ? 'Switched on. It will send the next time that happens.' : 'Switched off.');
      await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="page-head">
        <h1 className="h1">Notifications</h1>
        <span className="muted">
          {view.delivery.live
            ? `sending by ${view.delivery.driver} (${view.delivery.channel})`
            : 'nothing is being sent yet'}
        </span>
      </div>

      {!view.delivery.live && (
        <p className="notice">
          Messages are being written down but not sent: no mail service is set up yet. Everything queued
          appears in the outbox, so you can see exactly what would have gone out. Switching on sending is a
          server setting, not something to change here.
        </p>
      )}

      {note && <p className="notice">{note}</p>}
      {error && <p className="auth__error">{error}</p>}

      <div className="tabs">
        {[['templates', 'Messages'], ['outbox', 'Outbox']].map(([key, label]) => (
          <button key={key} className={`tabs__tab ${tab === key ? 'is-on' : ''}`} onClick={() => set('tab', key)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'templates' && !editing && (
        <>
          <table className="grid">
            <thead>
              <tr>
                <th>Message</th><th>When it is sent</th><th>To</th>
                {view.delivery.needsWhatsappTemplate && <th>WhatsApp template</th>}
                <th>Sent</th><th>On</th><th />
              </tr>
            </thead>
            <tbody>
              {view.templates.map((t) => (
                <tr key={t.id} className={t.active ? '' : 'is-off'}>
                  <td>
                    {t.name}
                    {t.unsupportedTags.length > 0 && (
                      <div className="warn small">Unknown tags: {t.unsupportedTags.join(' ')}</div>
                    )}
                  </td>
                  <td>
                    {t.eventLabel}
                    {!t.known && <div className="warn small">Learnica never raises this, so it would never send.</div>}
                  </td>
                  <td className="muted">{RECIPIENTS[t.recipient] ?? t.recipient}</td>
                  {view.delivery.needsWhatsappTemplate && (
                    <td>
                      {t.whatsappTemplate
                        ? <span className="mono small">{t.whatsappTemplate}</span>
                        : <span className="warn small">none — WhatsApp will refuse it</span>}
                    </td>
                  )}
                  <td className="num">{t.sent.sent ?? 0}</td>
                  <td>
                    <label className="builder__toggle">
                      <input type="checkbox" checked={t.active} disabled={busy || !t.known}
                        onChange={() => void toggle(t.id, !t.active)} />
                      <span>{t.active ? 'on' : 'off'}</span>
                    </label>
                  </td>
                  <td><button className="link-plain" onClick={() => set('edit', String(t.id))}>edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted small">
            Every message arrived switched off from TalentLMS. Nothing goes out until you switch it on, and a
            message for something Learnica does not do cannot be switched on at all.
          </p>
        </>
      )}

      {tab === 'templates' && editing && (
        <TemplateEditor
          id={Number(editing)} tags={view.tags} events={view.events}
          onDone={(m) => { setNote(m); set('edit', ''); void load(); }}
          onCancel={() => set('edit', '')}
        />
      )}

      {tab === 'outbox' && <Outbox live={view.delivery.live} />}
    </>
  );
}

function TemplateEditor({ id, tags, events, onDone, onCancel }: {
  id: number;
  tags: Record<string, string>;
  events: { event: string; label: string }[];
  onDone: (note: string) => void;
  onCancel: () => void;
}) {
  const [t, setT] = useState<NotificationTemplate | null>(null);
  const [preview, setPreview] = useState<{ body: string; subject: string | null; unsupportedTags: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try { setT((await api.get<{ template: NotificationTemplate }>(`/admin/notifications/${id}`)).template); }
      catch (e) { setError((e as ApiError).message); }
    })();
  }, [id]);

  if (error && !t) return <p className="auth__error">{error}</p>;
  if (!t) return <p className="muted">Loading…</p>;

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api.patch(`/admin/notifications/${id}`, {
        name: t.name, event: t.event, recipient: t.recipient, subject: t.subject, body: t.body,
        params: t.params?.whatsapp?.contentSid ? t.params : null,
      });
      onDone('Message saved.');
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const show = async () => {
    try { setPreview(await api.post('/admin/notifications/preview', { body: t.body, subject: t.subject ?? undefined })); }
    catch (e) { setError((e as ApiError).message); }
  };

  return (
    <section className="panel">
      <h2 className="h2">{t.name}</h2>
      {error && <p className="auth__error">{error}</p>}

      <div className="form">
        <label><span>Name</span>
          <input value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} /></label>
        <label><span>Sent when</span>
          <select value={t.event} onChange={(e) => setT({ ...t, event: e.target.value })}>
            {!events.some((x) => x.event === t.event) && <option value={t.event}>{t.event} (not raised by Learnica)</option>}
            {events.map((x) => <option key={x.event} value={x.event}>{x.label}</option>)}
          </select></label>
        <label><span>Sent to</span>
          <select value={t.recipient} onChange={(e) => setT({ ...t, recipient: e.target.value })}>
            {Object.entries(RECIPIENTS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select></label>
        <label><span>Subject</span>
          <input value={t.subject ?? ''} onChange={(e) => setT({ ...t, subject: e.target.value })} /></label>
        <label><span>Message</span>
          <textarea rows={10} value={t.body} onChange={(e) => setT({ ...t, body: e.target.value })} /></label>

        <details className="tagref" open={!!t.params?.whatsapp?.contentSid}>
          <summary>WhatsApp template</summary>
          <p className="muted small">
            WhatsApp only allows a free message within 24 hours of the person writing to you. Everything
            Learnica sends is outside that, so it must be a template you had approved in advance. Put its
            Twilio content id here, and list what fills its placeholders in order.
          </p>
          <label><span>Content id</span>
            <input value={t.params?.whatsapp?.contentSid ?? ''} placeholder="HX…"
              onChange={(e) => setT({
                ...t,
                params: {
                  ...(t.params ?? {}),
                  whatsapp: {
                    contentSid: e.target.value,
                    variables: t.params?.whatsapp?.variables ?? [],
                  },
                },
              })} /></label>
          <label><span>Placeholders, in order</span>
            <input value={(t.params?.whatsapp?.variables ?? []).join(', ')}
              placeholder="{first_name}, {course_name}"
              onChange={(e) => setT({
                ...t,
                params: {
                  ...(t.params ?? {}),
                  whatsapp: {
                    contentSid: t.params?.whatsapp?.contentSid ?? '',
                    variables: e.target.value.split(',').map((x) => x.trim()).filter(Boolean),
                  },
                },
              })} /></label>
        </details>

        <details className="tagref">
          <summary>What you can put in the message</summary>
          <ul className="plain">
            {Object.entries(tags).map(([tag, example]) => (
              <li key={tag}><code>{tag}</code> <span className="muted">— {example}</span></li>
            ))}
          </ul>
        </details>

        <div className="row">
          <button className="btn btn--primary" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button className="btn btn--ghost" onClick={() => void show()}>Preview</button>
          <button className="btn btn--ghost" onClick={onCancel}>Back</button>
        </div>
      </div>

      {preview && (
        <div className="panel">
          <h3 className="h3">How it will read</h3>
          {preview.subject && <p><strong>{preview.subject}</strong></p>}
          <p className="submission">{preview.body}</p>
          {preview.unsupportedTags.length > 0 && (
            <p className="warn">
              These are not filled in and will print as nothing: {preview.unsupportedTags.join(' ')}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function Outbox({ live }: { live: boolean }) {
  const [data, setData] = useState<{ messages: OutboxRow[]; totals: Record<string, number> } | null>(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(async () => {
    try { setData(await api.get(`/admin/notifications/outbox/list${status ? `?status=${status}` : ''}`)); }
    catch (e) { setError((e as ApiError).message); }
  }, [status]);
  useEffect(() => { void load(); }, [load]);

  if (error && !data) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  const act = async (path: string, said: string) => {
    setBusy(true); setError(null); setNote(null);
    try {
      const out = await api.post<Record<string, number>>(path);
      setNote(`${said} ${Object.entries(out).filter(([, v]) => typeof v === 'number').map(([k, v]) => `${v} ${k}`).join(', ')}.`);
      await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="filters">
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">Everything</option>
          <option value="pending">Waiting</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
        </select>
        <span className="muted">
          {data.totals.pending ?? 0} waiting · {data.totals.sent ?? 0} sent · {data.totals.failed ?? 0} failed
        </span>
        <button className="btn btn--ghost btn--small" disabled={busy}
          onClick={() => void act('/admin/notifications/outbox/send', 'Sending run finished:')}>
          {live ? 'Send what is waiting' : 'Run the sender now'}
        </button>
        <button className="btn btn--ghost btn--small" disabled={busy}
          onClick={() => void act('/admin/notifications/reminders/run', 'Reminders checked:')}>
          Check for reminders
        </button>
      </div>

      {note && <p className="notice">{note}</p>}
      {error && <p className="auth__error">{error}</p>}

      <table className="grid">
        <thead>
          <tr><th>To</th><th>Message</th><th>Queued</th><th>Status</th><th /></tr>
        </thead>
        <tbody>
          {data.messages.length === 0 && <tr><td colSpan={5} className="muted">Nothing here.</td></tr>}
          {data.messages.map((m) => (
            <tr key={m.id}>
              <td>
                {m.firstName} {m.lastName}
                <div className="muted small mono">{m.address ?? 'no address'}</div>
              </td>
              <td>
                {m.templateName ?? m.subject}
                <div className="muted small">{m.subject}</div>
                {open === m.id && <p className="submission">{m.body}</p>}
              </td>
              <td>{day(m.createdAt)}</td>
              <td>
                {m.status === 'sent' && <span className="pill pill--completed">sent {day(m.sentAt)}</span>}
                {m.status === 'pending' && <span className="pill pill--pending_review">waiting</span>}
                {m.status === 'failed' && <span className="pill pill--failed">failed</span>}
                {m.error && <div className="warn small">{m.error}</div>}
                {m.attempts > 1 && <div className="muted small">{m.attempts} attempts</div>}
              </td>
              <td className="row">
                <button className="link-plain" onClick={() => setOpen(open === m.id ? null : m.id)}>
                  {open === m.id ? 'hide' : 'read'}
                </button>
                {m.status === 'failed' && m.address && (
                  <button className="link-plain" disabled={busy}
                    onClick={() => void act(`/admin/notifications/outbox/${m.id}/retry`, 'Queued again:')}>
                    try again
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
